import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  MenuItem,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  RateReview as ReviewIcon,
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { jobDescriptionService } from '../services/jobDescriptionService';
import { feedbackService } from '../services/feedbackService';
import { useAuth } from '../hooks/useAuth';
import { COLORS } from '../config/colors';

interface JDEditDialogProps {
  jd: any;
  open: boolean;
  onClose: () => void;
  onSave: (updatedJD: any) => void;
  userRole?: string;
}

// Job Type Options
const JOB_TYPE_OPTIONS = [
  'Full-time',
  'Part-time',
  'Contract',
  'Temporary',
  'Internship',
  'Freelance',
];

// Job Category Options
const JOB_CATEGORY_OPTIONS = [
  'Technology',
  'Engineering',
  'Marketing',
  'Sales',
  'Finance',
  'Operations',
  'Human Resources',
  'Customer Service',
  'Legal',
  'Design',
  'Research',
  'Other',
];

// Employment Level Options
const EMPLOYMENT_LEVEL_OPTIONS = [
  'Entry Level',
  'Mid Level',
  'Senior',
  'Manager',
  'Director',
  'Executive',
];

// Education Level Options
const EDUCATION_LEVEL_OPTIONS = [
  'High School',
  'Associate',
  'Bachelor',
  'Master',
  'PhD',
  'Professional',
];

// Work Location Type Options
const WORK_LOCATION_TYPE_OPTIONS = ['Remote', 'On-site', 'Hybrid'];

// SME-specific JD review statuses - READ ONLY for display
const SME_REVIEW_STATUSES = [
  { value: 'Under Review', label: 'Under Review', color: COLORS.SUCCESS_DARK },
  { value: 'Reviewed and Ready', label: 'Reviewed and Ready to Post', color: COLORS.SUCCESS_DARK },
];

// Resume status options for SME
const RESUME_STATUS_OPTIONS = [
  { value: 'Selected', label: 'Selected', color: COLORS.SUCCESS_DARK },
  { value: 'Rejected', label: 'Rejected', color: COLORS.ERROR_DARK },
  { value: 'Shortlisted', label: 'Shortlisted', color: COLORS.PRIMARY },
  { value: 'On Hold', label: 'On Hold', color: COLORS.SUCCESS_DARK },
  { value: 'Under Review', label: 'Under Review', color: COLORS.GRAY_500 },
];

const JDEditDialog: React.FC<JDEditDialogProps> = ({
  jd,
  open,
  onClose,
  onSave,
  userRole = 'hr',
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<any>({});
  const [resumes, setResumes] = useState<any[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [updatingResume, setUpdatingResume] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  //  : JD Feedback modal state
  const [jdFeedbackModalOpen, setJdFeedbackModalOpen] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

  // Flatten nested fields for form state
  // Initialize form data from JD
  useEffect(() => {
    if (jd && open) {
      console.log('JD Edit Dialog - Received JD data:', jd);

      setFormData({
        // Basic fields
        jobId: jd.jobId || jd.id,
        jobTitle: jd.jobTitle || '',
        jobDescription: jd.jobDescription || '',
        jobType: jd.jobType || 'Full-time',
        jobCategory: jd.jobCategory || 'Technology',
        department: jd.department || '',
        employmentLevel: jd.employmentLevel || 'Mid Level',
        industry: jd.industry || '',
        jobStatus: jd.jobStatus || 'Open',
        postingDate: jd.postingDate || new Date().toISOString().split('T')[0],
        closingDate: jd.closingDate || '',
        numberOfOpenings: jd.numberOfOpenings || 1,
        jobReferenceCode: jd.jobReferenceCode || '',
        assigneeEmail: jd.assigneeEmail || '',
        assigneeName: jd.assigneeName || '',

        // Nested objects - preserve the structure
        location: {
          workLocationType: jd.location?.workLocationType || 'Remote',
          country: jd.location?.country || 'USA',
          state: jd.location?.state || '',
          city: jd.location?.city || '',
          zipPostalCode: jd.location?.zipPostalCode || '',
          officeAddress: jd.location?.officeAddress || '',
          region: jd.location?.region || '',
        },

        compensation: {
          salaryMin: jd?.compensation?.salaryMin || 0,
          salaryMax: jd?.compensation?.salaryMax || 0,
          salaryCurrency: jd.compensation?.salaryCurrency || 'USD',
          salaryType: jd.compensation?.salaryType || 'Annual',
          bonusEligibility: jd.compensation?.bonusEligibility || false,
          benefitsDescription: jd.compensation?.benefitsDescription || '',
          otherCompensation: jd.compensation?.otherCompensation || '',
        },

        requirements: {
          minimumEducationLevel: jd.requirements?.minimumEducationLevel || 'Bachelor',
          requiredExperienceYears: jd.requirements?.requiredExperienceYears || null,
          preferredExperienceYears: jd.requirements?.preferredExperienceYears || null,
          requiredSkills: jd.requirements?.requiredSkills || [],
          preferredSkills: jd.requirements?.preferredSkills || [],
          certificationsRequired: jd.requirements?.certificationsRequired || [],
          languagesRequired: jd.requirements?.languagesRequired || ['English'],
          travelRequirements: jd.requirements?.travelRequirements || '',
        },

        applicationDetails: {
          applicationURL: jd.applicationDetails?.applicationURL || '',
          applicationEmail: jd.applicationDetails?.applicationEmail || '',
          applicationDeadline: jd.applicationDetails?.applicationDeadline || '',
          applicationInstructions: jd.applicationDetails?.applicationInstructions || '',
          recruiterName: jd.applicationDetails?.recruiterName || '',
          recruiterEmail: jd.applicationDetails?.recruiterEmail || '',
          recruiterPhone: jd.applicationDetails?.recruiterPhone || '',
        },

        // SME specific
        smeReviewStatus: jd.smeReviewStatus || 'Under Review',
      });

      // SME feedback status
      const isReviewed = jd.smeReviewStatus === 'Reviewed and Ready';
      setHasSubmittedFeedback(isReviewed);

      // Load resumes for SME
      if (userRole === 'sme') {
        loadResumesForJD(jd.jobId || jd.id);
      }

      setError(null);
    }
  }, [jd, open, userRole]);

  const loadResumesForJD = async (jobId: string) => {
    setLoadingResumes(true);
    try {
      const resumeData = await jobDescriptionService.getResumesForJD(jobId);
      setResumes(resumeData.resumes || resumeData.data || []);
    } catch (err: any) {
      console.error('Error loading resumes:', err);
      setError('Failed to load resumes for this job description.');
      setResumes([]);
    } finally {
      setLoadingResumes(false);
    }
    setLoadingResumes(false);
    //  setLoading(false);
  };

  // Handle nested object field updates
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field.includes('.')) {
        // Handle nested fields like 'location.country', 'compensation?.salaryMin'
        const [objectKey, fieldKey] = field.split('.');
        return {
          ...prev,
          [objectKey]: {
            ...prev[objectKey],
            [fieldKey]: value,
          },
        };
      } else {
        // Handle top-level fields
        return {
          ...prev,
          [field]: value,
        };
      }
    });
  };

  // Handle array fields like skills with nested object support
  const handleArrayFieldChange = (field: string, value: string) => {
    const skillsArray = value
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill);
    handleInputChange(field, skillsArray);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (userRole === 'sme') {
        // For SME, only update if they haven't submitted feedback yet
        if (!hasSubmittedFeedback) {
          setError('Please submit feedback to update the review status.');
          return;
        }

        const updatedData = {
          ...jd,
          smeReviewStatus: 'Reviewed and Ready',
          reviewedBy: user?.username || user?.email || 'SME User',
          reviewedAt: new Date().toISOString(),
        };
        onSave(updatedData);
      } else {
        // For HR, use nested structure to match API format
        const payload = {
          jobTitle: formData.jobTitle,
          jobDescription: formData.jobDescription,
          jobType: formData.jobType,
          jobCategory: formData.jobCategory,
          department: formData.department,
          employmentLevel: formData.employmentLevel,
          industry: formData.industry,
          jobStatus: formData.jobStatus,
          postingDate: formData.postingDate,
          closingDate: formData.closingDate,
          numberOfOpenings: Number(formData.numberOfOpenings),
          jobReferenceCode: formData.jobReferenceCode,
          assigneeEmail: formData.assigneeEmail,
          assigneeName: formData.assigneeName,

          location: {
            workLocationType: formData.location.workLocationType,
            country: formData.location.country,
            state: formData.location.state,
            city: formData.location.city,
            zipPostalCode: formData.location.zipPostalCode,
            officeAddress: formData.location.officeAddress,
            region: formData.location.region,
          },

          compensation: {
            salaryMin: Number(formData.compensation?.salaryMin),
            salaryMax: Number(formData.compensation?.salaryMax),
            salaryCurrency: formData.compensation?.salaryCurrency,
            salaryType: formData.compensation?.salaryType,
            bonusEligibility: formData.compensation?.bonusEligibility,
            benefitsDescription: formData.compensation?.benefitsDescription,
            otherCompensation: formData.compensation?.otherCompensation,
          },

          requirements: {
            minimumEducationLevel: formData.requirements?.minimumEducationLevel,
            requiredExperienceYears: Number(formData.requirements?.requiredExperienceYears) || null,
            preferredExperienceYears:
              Number(formData.requirements?.preferredExperienceYears) || null,
            requiredSkills: formData.requirements?.requiredSkills,
            preferredSkills: formData.requirements?.preferredSkills,
            certificationsRequired: formData.requirements?.certificationsRequired,
            languagesRequired: formData.requirements?.languagesRequired,
            travelRequirements: formData.requirements?.travelRequirements,
          },

          applicationDetails: {
            applicationURL: formData.applicationDetails.applicationURL,
            applicationEmail: formData.applicationDetails.applicationEmail,
            applicationDeadline: formData.applicationDetails.applicationDeadline,
            applicationInstructions: formData.applicationDetails.applicationInstructions,
            recruiterName: formData.applicationDetails.recruiterName,
            recruiterEmail: formData.applicationDetails.recruiterEmail,
            recruiterPhone: formData.applicationDetails.recruiterPhone,
          },

          company: formData.company,
          internal: formData.internal,
          advanced: formData.advanced,
        };

        console.log('Saving job description:', payload);

        const result = await jobDescriptionService.update(formData.jobId, payload);
        console.log('Job description updated successfully:', result);

        onSave(result);
      }
    } catch (err: any) {
      console.error('Error saving JD:', err);
      setError(`Failed to save changes: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  //  : Handle JD feedback submission
  const handleJDFeedbackSubmit = async (feedbackData: any) => {
    try {
      const jobId = jd?.jobId || jd?.id;

      if (userRole === 'sme') {
        await feedbackService.submitSMEJobFeedback(jobId, feedbackData);
      } else if (userRole === 'hr' || userRole === 'admin') {
        await feedbackService.submitHRJobFeedback(jobId, feedbackData);
      }

      // Update the JD status if SME
      if (userRole === 'sme') {
        const updatedJD = {
          ...jd,
          smeReviewStatus: 'Reviewed and Ready',
          reviewedBy: user?.username || user?.email || 'SME User',
          reviewedAt: new Date().toISOString(),
        };
        setFormData(updatedJD);
        setHasSubmittedFeedback(true);
        await onSave(updatedJD);
      }

      // Close feedback modal
      setJdFeedbackModalOpen(false);
      setError(null);
    } catch (error) {
      console.error('Error submitting JD feedback:', error);
      setError(`Failed to submit feedback: ${error.message}`);
    }
  };

  const handleResumeStatusUpdate = async (resumeId: string, newStatus: string) => {
    setUpdatingResume(resumeId);
    try {
      await jobDescriptionService.updateResumeStatus(resumeId, newStatus);

      // Update local state
      setResumes(prev =>
        prev.map(resume =>
          resume.id === resumeId
            ? { ...resume, status: newStatus, updatedAt: new Date().toISOString() }
            : resume
        )
      );
    } catch (err: any) {
      console.error('Error updating resume status:', err);
      setError(`Failed to update resume status: ${err.message}`);
    } finally {
      setUpdatingResume(null);
    }
  };

  const getStatusChipProps = (status: string) => {
    const statusConfig = RESUME_STATUS_OPTIONS.find(opt => opt.value === status);
    return {
      color: statusConfig?.color || COLORS.GRAY_500,
      background: statusConfig?.color ? `${statusConfig.color}20` :  COLORS.GRAY_100 ,
    };
  };

  const getCurrentStatus = () => {
    return formData.smeReviewStatus || 'Under Review';
  };

  const getStatusColor = (status: string) => {
    const statusConfig = SME_REVIEW_STATUSES.find(s => s.value === status);
    return statusConfig?.color || COLORS.GRAY_500;
  };

  if (!jd) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'white',
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {userRole === 'sme' ? 'Review Job Description' : 'Edit Job Description'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.9, mt: 0.5, color: COLORS.PRIMARY_BLUE_DARK }}
            >
              {formData.jobId} - {formData.jobTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'var(--dark-gray)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Alert
              severity={error.includes('Please submit feedback') ? 'info' : 'error'}
              sx={{ m: 2, borderRadius: '8px' }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ p: 3 }}>
            {userRole === 'sme' ? (
              // SME View - Focus on review status (READ ONLY)
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'var(--primary-blue)' }}>
                  Review Status
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={8}>
                    <Box
                      sx={{
                        p: 3,
                        border: '2px solid #e0e0e0',
                        borderRadius: '12px',
                        background: hasSubmittedFeedback ? '#f1f8e9' : COLORS.WARNING_LIGHT,
                        borderColor: hasSubmittedFeedback ? '#4caf50' : '#ff9800',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {hasSubmittedFeedback ? (
                          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        ) : (
                          <ReviewIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                        )}
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {hasSubmittedFeedback ? 'Review Completed' : 'Review Pending'}
                          </Typography>
                          <Chip
                            label={getCurrentStatus()}
                            sx={{
                              backgroundColor: getStatusColor(getCurrentStatus()),
                              color: 'white',
                              fontWeight: 600,
                              mt: 1,
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {hasSubmittedFeedback
                          ? 'You have successfully reviewed this job description and submitted your feedback.'
                          : 'Please review the job description details below and submit your professional feedback.'}
                      </Typography>

                      {!hasSubmittedFeedback && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Complete your review by clicking "Submit Feedback" below. This will
                          automatically mark the JD as "Reviewed and Ready".
                        </Alert>
                      )}
                    </Box>
                  </Grid>

                  {hasSubmittedFeedback && (
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid #4caf50',
                          borderRadius: '8px',
                          background: '#f1f8e9',
                          textAlign: 'center',
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 48, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.SUCCESS_DARK }}>
                          Feedback Submitted
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.reviewedAt
                            ? new Date(formData.reviewedAt).toLocaleDateString()
                            : 'Recently reviewed'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Job Description Display (Read-only for SME) */}
                <Typography variant="h6" sx={{ mb: 2, color: 'var(--primary-blue)' }}>
                  Job Description Details
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Job Title
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formData.jobTitle}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Job ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formData.jobId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {formData.jobDescription || 'No description available'}
                        </Typography>
                      </Grid>
                      {formData.location?.city && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {[
                              formData.location.city,
                              formData.location.state,
                              formData.location.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              // HR View - Full edit capabilities
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: COLORS.PRIMARY_DARK }}>
                  Job Details
                </Typography>

                <Grid container spacing={2}>
                  {/* Basic Job Information */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={formData.jobTitle || ''}
                      onChange={e => handleInputChange('jobTitle', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Job ID"
                      value={formData.jobId || ''}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Job Description"
                      value={formData.jobDescription || ''}
                      onChange={e => handleInputChange('jobDescription', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>

                  {/* Job Classification */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Job Type"
                      value={formData.jobType || 'Full-time'}
                      onChange={e => handleInputChange('jobType', e.target.value)}
                      variant="outlined"
                    >
                      {JOB_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Job Category"
                      value={formData.jobCategory || 'Technology'}
                      onChange={e => handleInputChange('jobCategory', e.target.value)}
                      variant="outlined"
                    >
                      {JOB_CATEGORY_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Employment Level"
                      value={formData.employmentLevel || 'Mid Level'}
                      onChange={e => handleInputChange('employmentLevel', e.target.value)}
                      variant="outlined"
                    >
                      {EMPLOYMENT_LEVEL_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={formData.department || ''}
                      onChange={e => handleInputChange('department', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Industry"
                      value={formData.industry || ''}
                      onChange={e => handleInputChange('industry', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Openings"
                      value={formData.numberOfOpenings || 1}
                      onChange={e => handleInputChange('numberOfOpenings', Number(e.target.value))}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Posting Date"
                      value={formData.postingDate || ''}
                      onChange={e => handleInputChange('postingDate', e.target.value)}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Closing Date"
                      value={formData.closingDate || ''}
                      onChange={e => handleInputChange('closingDate', e.target.value)}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Location Section */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: COLORS.PRIMARY_DARK }}>
                  Location Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Work Location Type"
                      value={formData.location?.workLocationType || 'Remote'}
                      onChange={e => handleInputChange('location.workLocationType', e.target.value)}
                      variant="outlined"
                    >
                      {WORK_LOCATION_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.location?.country || ''}
                      onChange={e => handleInputChange('location.country', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.location?.state || ''}
                      onChange={e => handleInputChange('location.state', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.location?.city || ''}
                      onChange={e => handleInputChange('location.city', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Office Address"
                      value={formData.location?.officeAddress || ''}
                      onChange={e => handleInputChange('location.officeAddress', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Zip/Postal Code"
                      value={formData.location?.zipPostalCode || ''}
                      onChange={e => handleInputChange('location.zipPostalCode', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Region"
                      value={formData.location?.region || ''}
                      onChange={e => handleInputChange('location.region', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                {/* Compensation Section */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: COLORS.PRIMARY_DARK }}>
                  Compensation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Salary Min"
                      value={formData.compensation?.salaryMin || 0}
                      onChange={e =>
                        handleInputChange('compensation?.salaryMin', Number(e.target.value))
                      }
                      variant="outlined"
                      inputProps={{
                        min: 0,
                        step: 1000,
                      }}
                      error={formData.compensation?.salaryMin < 0}
                      helperText={formData.compensation?.salaryMin < 0 ? 'Cannot be negative' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Salary Max"
                      value={formData.compensation?.salaryMax || 0}
                      onChange={e =>
                        handleInputChange('compensation?.salaryMax', Number(e.target.value))
                      }
                      variant="outlined"
                      inputProps={{
                        min: 0,
                        step: 1000,
                      }}
                      error={
                        formData.compensation?.salaryMax < 0 ||
                        (formData.compensation?.salaryMax > 0 &&
                          formData.compensation?.salaryMax < formData.compensation?.salaryMin)
                      }
                      helperText={
                        formData.compensation?.salaryMax < 0
                          ? 'Cannot be negative'
                          : formData.compensation?.salaryMax > 0 &&
                              formData.compensation?.salaryMax < formData.compensation?.salaryMin
                            ? 'Maximum must be greater than minimum'
                            : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Currency"
                      value={formData.compensation?.salaryCurrency || 'USD'}
                      onChange={e =>
                        handleInputChange('compensation?.salaryCurrency', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Salary Type"
                      value={formData.compensation?.salaryType || 'Annual'}
                      onChange={e => handleInputChange('compensation?.salaryType', e.target.value)}
                      variant="outlined"
                    >
                      <MenuItem value="Hourly">Hourly</MenuItem>
                      <MenuItem value="Annual">Annual</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Benefits Description"
                      value={formData.compensation?.benefitsDescription || ''}
                      onChange={e =>
                        handleInputChange('compensation?.benefitsDescription', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Other Compensation"
                      value={formData.compensation?.otherCompensation || ''}
                      onChange={e =>
                        handleInputChange('compensation?.otherCompensation', e.target.value)
                      }
                      variant="outlined"
                      placeholder="Stock options, equity, etc."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.compensation?.bonusEligibility || false}
                          onChange={e =>
                            handleInputChange('compensation?.bonusEligibility', e.target.checked)
                          }
                        />
                      }
                      label="Bonus Eligibility"
                    />
                  </Grid>
                </Grid>

                {/* Requirements Section */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: COLORS.PRIMARY_DARK }}>
                  Requirements
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Minimum Education Level"
                      value={formData.requirements?.minimumEducationLevel || 'Bachelor'}
                      onChange={e =>
                        handleInputChange('requirements?.minimumEducationLevel', e.target.value)
                      }
                      variant="outlined"
                    >
                      {EDUCATION_LEVEL_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Required Experience (Years)"
                      value={formData.requirements?.requiredExperienceYears || ''}
                      onChange={e =>
                        handleInputChange(
                          'requirements?.requiredExperienceYears',
                          Number(e.target.value) || null
                        )
                      }
                      variant="outlined"
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      error={formData.requirements?.requiredExperienceYears < 0}
                      helperText={
                        formData.requirements?.requiredExperienceYears < 0
                          ? 'Cannot be negative'
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Required Skills (comma separated)"
                      value={formData.requirements?.requiredSkills?.join(', ') || ''}
                      onChange={e =>
                        handleArrayFieldChange('requirements?.requiredSkills', e.target.value)
                      }
                      variant="outlined"
                      placeholder="JavaScript, React, Node.js"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Preferred Skills (comma separated)"
                      value={formData.requirements?.preferredSkills?.join(', ') || ''}
                      onChange={e =>
                        handleArrayFieldChange('requirements?.preferFredSkills', e.target.value)
                      }
                      variant="outlined"
                      placeholder="AWS, Docker, Kubernetes"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Preferred Experience (Years)"
                      value={formData.requirements?.preferredExperienceYears || ''}
                      onChange={e =>
                        handleInputChange(
                          'requirements?.preferredExperienceYears',
                          Number(e.target.value) || null
                        )
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Travel Requirements"
                      value={formData.requirements?.travelRequirements || ''}
                      onChange={e =>
                        handleInputChange('requirements?.travelRequirements', e.target.value)
                      }
                      variant="outlined"
                      placeholder="None, Occasional, Frequent"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Languages Required (comma separated)"
                      value={formData.requirements?.languagesRequired?.join(', ') || ''}
                      onChange={e =>
                        handleArrayFieldChange('requirements?.languagesRequired', e.target.value)
                      }
                      variant="outlined"
                      placeholder="English, Spanish, French"
                    />
                  </Grid>
                </Grid>

                {/* Application Details */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: COLORS.PRIMARY }}>
                  Application Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Application URL"
                      value={formData.applicationDetails?.applicationURL || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.applicationURL', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Application Email"
                      value={formData.applicationDetails?.applicationEmail || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.applicationEmail', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Recruiter Name"
                      value={formData.applicationDetails?.recruiterName || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.recruiterName', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Recruiter Email"
                      value={formData.applicationDetails?.recruiterEmail || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.recruiterEmail', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Recruiter Phone"
                      value={formData.applicationDetails?.recruiterPhone || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.recruiterPhone', e.target.value)
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Application Deadline"
                      value={formData.applicationDetails?.applicationDeadline || ''}
                      onChange={e =>
                        handleInputChange('applicationDetails.applicationDeadline', e.target.value)
                      }
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Application Instructions"
                      value={formData.applicationDetails?.applicationInstructions || ''}
                      onChange={e =>
                        handleInputChange(
                          'applicationDetails.applicationInstructions',
                          e.target.value
                        )
                      }
                      variant="outlined"
                      placeholder="Apply online"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Resumes Section - Only for SME */}
            {userRole === 'sme' && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: 'var(--primary-blue)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PersonIcon />
                  Uploaded Resumes ({resumes.length})
                </Typography>

                {loadingResumes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : resumes.length > 0 ? (
                  <Box
                    sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}
                  >
                    <Table size="small">
                      <TableHead sx={{ backgroundColor:  COLORS.GRAY_100  }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Candidate Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Current Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Change Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumes.map((resume, index) => (
                          <TableRow key={resume.id || index} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {resume.candidateName || resume.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {resume.email || resume.candidateEmail || 'No email'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={resume.status || 'Under Review'}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusChipProps(
                                    resume.status || 'Under Review'
                                  ).background,
                                  color: getStatusChipProps(resume.status || 'Under Review').color,
                                  fontWeight: 600,
                                  borderRadius: '12px',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                value={resume.status || 'Under Review'}
                                onChange={e => handleResumeStatusUpdate(resume.id, e.target.value)}
                                disabled={updatingResume === resume.id}
                                sx={{ minWidth: 140 }}
                              >
                                {RESUME_STATUS_OPTIONS.map(option => (
                                  <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: option.color,
                                        }}
                                      />
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </TextField>
                              {updatingResume === resume.id && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {resume.uploadedDate || resume.createdAt
                                  ? new Date(
                                      resume.uploadedDate || resume.createdAt
                                    ).toLocaleDateString()
                                  : 'Unknown'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      border: '2px dashed #e0e0e0',
                      borderRadius: '8px',
                      background: '#fafafa',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No resumes uploaded for this job description yet.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              color: '#666666',
              borderRadius: '8px',
              px: 3,
            }}
          >
            {hasSubmittedFeedback ? 'Close' : 'Cancel'}
          </Button>

          {/* Save/Update button - only for HR users */}
          {userRole !== 'sme' && (
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={saving}
              sx={{
                borderRadius: '8px',
                px: 3,
                backgroundColor: COLORS.PRIMARY_DARK,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {saving ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JDEditDialog;
